/**
 * The cookie banner's pure rules, kept apart from the service so they can be
 * tested without Angular or the i18n catalog behind it.
 */

/** What a category's answer can be: yes, no, or not answered yet — `null` once the
 *  cookies have been read, and missing entirely before that. */
export type CookieChoice = boolean | null | undefined;

/** The cookie categories this app has. Named so a typo in a list of ids, or a
 *  renamed category, is a build error rather than a silent no-op. */
export type CookieCategoryId = 'functionality' | 'security' | 'personalization' | 'ad' | 'analytics';

/** Only what a rule here needs to know about a category. */
export interface CookieCategoryShape {
  id: CookieCategoryId;
  required?: boolean;
}

/**
 * Every category, in the order the cookie dialog lists them.
 *
 * The ids and the required flag live here, with no Angular or i18n behind them,
 * so the rules and their tests read the SAME list the dialog does — a test that
 * kept its own copy would go on passing after a category was added.
 * `cookie-consent.service.ts` adds the translated name and description.
 */
export const COOKIE_CATEGORIES: readonly CookieCategoryShape[] = [
  { id: 'functionality', required: true },
  { id: 'security' },
  { id: 'personalization' },
  { id: 'ad' },
  { id: 'analytics' },
];

/**
 * What one `_cf_consent_<id>` cookie value means (#5712).
 *
 * A MISSING cookie is not an answer, so it reads as `null` and the toggle shows
 * neutral — a category the person has never been asked about must not be drawn
 * as a decision they made. Anything stored but unreadable is treated the same
 * way, which is the cautious direction: it asks rather than assumes.
 */
export function cookieValueToChoice(stored: string | undefined): boolean | null {
  if (stored === '1') return true;
  if (stored === '0') return false;
  return null;
}

/**
 * Whether every optional category has been answered, so the choices may be saved
 * (#5712).
 *
 * A category nobody has answered is held as `null` and its toggle draws neutral,
 * so a first-time visitor is never shown a decision made for them. Saving has to
 * wait until each one is set, because saving writes a "no" for anything that is
 * not a yes — and a "no" the person never chose is exactly what the neutral state
 * exists to avoid. Required categories are never in question.
 */
export function everyCookieChoiceMade(
  values: { [id: string]: CookieChoice },
  categories: readonly CookieCategoryShape[],
): boolean {
  return categories.every(c => c.required || typeof values[c.id] === 'boolean');
}
